import { useState, useMemo, useCallback } from 'react';
import { useQueryClient } from 'react-query';
import { useToastContext } from '@/contexts/toastContext';
import useApiFetch from '@/hooks/useApiFetch';
import useApiSubmit from '@/hooks/useApiSubmit';
import { getActiveOrgId } from '@/lib/activeOrg';
import financeCategoriesService from '@/services/financeCategories';
import financeVendorsService from '@/services/financeVendors';
import { FetchApiIds } from '@/types';
import { FxVendor, FxCategory } from '@/types/api/finance';
import FinanceXVendorsComponent from './FinanceXVendors.component';

const FinanceXVendorsContainer = function (): JSX.Element {
  const queryClient = useQueryClient();
  const toast = useToastContext();
  const orgId = getActiveOrgId();
  const [showForm, setShowForm] = useState(false);
  const [editingVendor, setEditingVendor] = useState<FxVendor | null>(null);
  const [search, setSearch] = useState('');

  const vendorsResponse = useApiFetch<{ data: FxVendor[] }, void>(
    [FetchApiIds.FX_VENDORS, orgId, search],
    async () => financeVendorsService.getAll({ page: 1, take: 100, search: search || undefined }),
    { retry: false }
  );

  const categoriesResponse = useApiFetch<FxCategory[], void>(
    [FetchApiIds.FX_CATEGORIES, orgId],
    async () => financeCategoriesService.getAll(),
    { retry: false }
  );

  const vendors = useMemo(() => (vendorsResponse.data as any)?.data ?? [], [vendorsResponse.data]);

  const categories = useMemo(
    () => (categoriesResponse.data as FxCategory[]) ?? [],
    [categoriesResponse.data]
  );

  const createMutation = useApiSubmit<FxVendor, any>(
    (payload) => financeVendorsService.create(payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(FetchApiIds.FX_VENDORS);
        setShowForm(false);
        toast.showSuccessFeedback('Vendor created.');
      },
    }
  );

  const updateMutation = useApiSubmit<FxVendor, any>(
    (payload) => financeVendorsService.update(payload.id, payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(FetchApiIds.FX_VENDORS);
        setShowForm(false);
        setEditingVendor(null);
        toast.showSuccessFeedback('Vendor updated.');
      },
    }
  );

  const deleteMutation = useApiSubmit<FxVendor, any>(
    (payload) => financeVendorsService.delete(payload.id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(FetchApiIds.FX_VENDORS);
        toast.showSuccessFeedback('Vendor deleted.');
      },
    }
  );

  const handleCreate = useCallback(
    (data: any) => {
      createMutation.mutate(data);
    },
    [createMutation]
  );

  const handleUpdate = useCallback(
    (data: any) => {
      updateMutation.mutate({ id: editingVendor?.id, ...data });
    },
    [updateMutation, editingVendor]
  );

  const handleDelete = useCallback(
    (vendor: FxVendor) => {
      deleteMutation.mutate({ id: vendor.id });
    },
    [deleteMutation]
  );

  const handleEdit = useCallback((vendor: FxVendor) => {
    setEditingVendor(vendor);
    setShowForm(true);
  }, []);

  const handleCloseForm = useCallback(() => {
    setShowForm(false);
    setEditingVendor(null);
  }, []);

  return (
    <FinanceXVendorsComponent
      vendors={vendors}
      categories={categories}
      isLoading={vendorsResponse.isLoading}
      showForm={showForm}
      editingVendor={editingVendor}
      search={search}
      onSearchChange={setSearch}
      onNewVendor={() => setShowForm(true)}
      onEdit={handleEdit}
      onDelete={handleDelete}
      onSubmit={editingVendor ? handleUpdate : handleCreate}
      onCloseForm={handleCloseForm}
      isSubmitting={createMutation.isLoading || updateMutation.isLoading}
    />
  );
};

export default FinanceXVendorsContainer;
